// "Import stores": port of CSVImportExportView's import and CSVImportSheet (GroundTeam/Views/Settings/
// IntegrationsView.swift) for admins and managers. Choose a CSV file → it is read and checked in the browser → review
// (what will be added, what is skipped and why, owner / lead source / record type, the first rows) → "Import N
// stores" saves it in batches with a progress bar and Stop. The rules are in domain/importRecords.ts, the words and the
// save loop in importModel.ts; this file is the form. Opened from Stores › Import and from the map (toolbar and the
// empty map).
//
// Unlike iOS there is no geocoding: rows without coordinates are imported without a pin, and the review says so.
// A refused batch keeps the dialog open with the reason, the file and the options, so Import can simply be pressed
// again (the stores already saved then count as "Already in Ground Team").

import { FileUp, TriangleAlert, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { RECORD_TYPE_PLURALS, RECORD_TYPES } from '@/contracts/enums';
import { useLeadSources, usePipelines, useRecords, useTerritories, useViewer, useVisibleUsers } from '@/data/hooks';
import { formatGrouped } from '@/domain/format';
import {
  defaultImportSource,
  importPatchBuilder,
  importTypeCounts,
  parseImportCsv,
  planImport,
  unknownSourceNames,
  type ImportPlan,
  type ParsedImport,
} from '@/domain/importRecords';
import { pickerUsers } from '@/domain/team';
import { useSave } from '@/sync/hooks';
import {
  Button,
  Checkbox,
  DataTable,
  Dialog,
  Field,
  KeyValueRow,
  ProgressBar,
  Select,
  cn,
  createColumnHelper,
  useToast,
  type DataTableColumn,
} from '@/ui';
import {
  IMPORT_COLUMNS_HINT,
  IMPORT_DESCRIPTION,
  IMPORT_TITLE,
  NO_ROWS_TEXT,
  OWNER_PLACEHOLDER,
  PREVIEW_ROW_LIMIT,
  READ_ERROR_TEXT,
  SOURCE_HINT,
  STREET_NAMES_CONFIRM,
  STREET_NAMES_TEXT,
  TYPE_FROM_FILE_VALUE,
  TYPE_HINT,
  columnUsage,
  decodeImportText,
  defaultOwnerValue,
  importButtonLabel,
  importProgressText,
  importResultToast,
  nameColumnProblem,
  noCoordinatesText,
  nothingToAddText,
  ownerHint,
  ownerIdFromValue,
  ownerOptions,
  planSummary,
  previewRows,
  runImport,
  sourceOptions,
  typeOptions,
  typeOverrideFromValue,
  unknownSourcesText,
  type PreviewRow,
} from './importModel';

export interface ImportStoresDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ImportStoresDialog(props: ImportStoresDialogProps) {
  // Mounted only while open, so every opening starts from a clean form.
  if (!props.open) return null;
  return <OpenImportDialog onClose={props.onClose} />;
}

interface LoadedFile {
  name: string;
  parsed: ParsedImport;
}

function OpenImportDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const save = useSave();
  const viewer = useViewer();
  const records = useRecords();
  const visibleUsers = useVisibleUsers();
  const sources = useLeadSources();
  const pipelines = usePipelines();
  const territories = useTerritories();

  const people = useMemo(() => pickerUsers(visibleUsers.values), [visibleUsers]);
  const fileInput = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<LoadedFile | null>(null);
  const [reading, setReading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [owner, setOwner] = useState(() => defaultOwnerValue(people, viewer?.profileId));
  // No name or company column: naming every store after its street needs a tick first (reset with each new file).
  const [streetNamesOk, setStreetNamesOk] = useState(false);
  const [sourceId, setSourceId] = useState(() => defaultImportSource(sources.values)?.id ?? '');
  const [typeValue, setTypeValue] = useState(TYPE_FROM_FILE_VALUE);
  // While saving, the plan taken when Import was pressed (the live one would count the new stores as duplicates).
  const [running, setRunning] = useState<{ plan: ImportPlan; done: number } | null>(null);
  const stopRequested = useRef(false);
  const [stopping, setStopping] = useState(false);
  const inFlight = useRef(false);

  // The dialog going away (navigation) stops the loop between batches; what was saved stays saved.
  useEffect(
    () => () => {
      stopRequested.current = true;
    },
    [],
  );

  // Every store in the replica, archived and undecodable ones included (iOS `existingDuplicateKeys`).
  const existing = useMemo(() => records.items.map((i) => i.value), [records]);
  const importing = running !== null;
  const livePlan = useMemo(() => (file && !importing ? planImport(file.parsed.rows, existing) : null), [file, existing, importing]);
  const plan = running?.plan ?? livePlan;

  const typeOverride = typeOverrideFromValue(typeValue);
  const typeCounts = useMemo(() => (plan ? importTypeCounts(plan.toAdd, typeOverride) : null), [plan, typeOverride]);
  const unknownSources = useMemo(() => (file ? unknownSourceNames(file.parsed.rows, sources.values) : []), [file, sources]);
  const usage = useMemo(() => (file ? columnUsage(file.parsed) : null), [file]);
  const preview = useMemo(() => (plan ? previewRows(plan.toAdd) : []), [plan]);
  const summary = useMemo(() => (plan ? planSummary(plan) : []), [plan]);

  const chooseFile = () => fileInput.current?.click();

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = ''; // choosing the same file again still fires a change
    if (!picked) return;
    setReading(true);
    setErrorText(null);
    setStreetNamesOk(false);
    try {
      // Strict UTF-8: another encoding lands in the catch (READ_ERROR_TEXT) instead of importing "Caf�".
      const parsed = parseImportCsv(decodeImportText(await picked.arrayBuffer()));
      if (parsed.rows.length === 0) {
        setFile(null);
        setErrorText(NO_ROWS_TEXT);
      } else {
        setFile({ name: picked.name, parsed });
      }
    } catch {
      setFile(null);
      setErrorText(READ_ERROR_TEXT);
    } finally {
      setReading(false);
    }
  };

  const nameProblem = file ? nameColumnProblem(file.parsed.columns) : null;
  const ownerId = ownerIdFromValue(owner);
  const ready = ownerId !== null && (nameProblem !== 'street' || streetNamesOk);

  const startImport = async () => {
    if (!plan || plan.toAdd.length === 0 || !ready || inFlight.current) return;
    inFlight.current = true;
    stopRequested.current = false;
    setErrorText(null);
    const frozen = plan;
    setRunning({ plan: frozen, done: 0 });
    const build = importPatchBuilder({
      ownerId,
      defaultSourceId: sourceId === '' ? null : sourceId,
      typeOverride,
      createdBy: viewer?.profileId ?? null,
      sources: sources.values,
      pipelines: pipelines.values,
      territories: territories.values,
      logCreation: true,
      now: new Date(),
    });
    const outcome = await runImport(frozen.toAdd, build, {
      save,
      onProgress: (done) => setRunning({ plan: frozen, done }),
      shouldStop: () => stopRequested.current,
    });
    inFlight.current = false;
    const result = importResultToast(outcome, frozen);
    if (outcome.error && !stopRequested.current) {
      // Keep the file and the options: the reason shows above the summary and Import can be pressed again.
      console.error('Import stopped: a batch was refused', outcome.error);
      setRunning(null);
      setStopping(false);
      setErrorText(result.message);
      return;
    }
    toast.show(result.tone, result.message, { duration: 10_000 });
    onClose();
  };

  const addCount = plan?.toAdd.length ?? 0;
  const nothing = file && plan ? nothingToAddText(plan, file.parsed.columns) : null;
  const sourceName = sourceId === '' ? null : (sources.get(sourceId)?.value.name ?? null);

  return (
    <Dialog
      open
      onClose={onClose}
      // Closing mid-import would hide the progress; Stop ends it between batches instead.
      dismissible={!importing}
      hideCloseButton={importing}
      title={IMPORT_TITLE}
      description={IMPORT_DESCRIPTION}
      size="lg"
      footer={
        importing ? (
          <Button
            variant="secondary"
            loading={stopping}
            onClick={() => {
              stopRequested.current = true;
              setStopping(true);
            }}
          >
            {stopping ? 'Stopping…' : 'Stop'}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            {file && (
              <Button variant="primary" icon={<Upload />} onClick={() => void startImport()} disabled={addCount === 0 || !ready}>
                {importButtonLabel(addCount)}
              </Button>
            )}
          </>
        )
      }
    >
      <input ref={fileInput} type="file" accept=".csv,text/csv" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => void onFileChange(e)} />

      {importing && running ? (
        <div className="flex flex-col gap-3 py-2" aria-live="polite">
          <ProgressBar
            value={running.plan.toAdd.length > 0 ? running.done / running.plan.toAdd.length : 0}
            label="Importing stores"
            showLabel
            detail={importProgressText(running.done, running.plan.toAdd.length)}
            size="md"
          />
          <p className="text-xs text-fg-muted">Keep this tab open. Stores already imported stay saved if you stop.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant={file ? 'secondary' : 'primary'} icon={<FileUp />} onClick={chooseFile} loading={reading}>
                {file ? 'Choose another file' : 'Choose CSV file'}
              </Button>
              {file && (
                <span className="min-w-0 truncate text-sm font-medium text-fg" title={file.name}>
                  {file.name}
                </span>
              )}
            </div>
            <p className="text-xs text-fg-muted">{IMPORT_COLUMNS_HINT}</p>
          </div>

          {errorText && (
            <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger-text">
              {errorText}
            </p>
          )}

          {file && plan && typeCounts && (
            <>
              <section aria-label="Summary" className="rounded-card border border-line bg-surface-2 px-4 py-2">
                <dl className="divide-y divide-line">
                  {summary.slice(0, 2).map((s) => (
                    <KeyValueRow key={s.label} label={s.label} value={<span className="font-semibold">{formatGrouped(s.value)}</span>} />
                  ))}
                  {/* Will be added, per type (the record type option changes these). */}
                  {RECORD_TYPES.filter((t) => typeCounts[t] > 0).map((t) => (
                    <KeyValueRow key={t} label={<span className="pl-4">{RECORD_TYPE_PLURALS[t]}</span>} value={formatGrouped(typeCounts[t])} />
                  ))}
                  {summary.slice(2).map((s) => (
                    <KeyValueRow
                      key={s.label}
                      label={<span className={s.tone === 'warning' ? 'text-warning-text' : undefined}>{s.label}</span>}
                      value={formatGrouped(s.value)}
                    />
                  ))}
                </dl>
              </section>

              {nothing !== null &&
                (nameProblem === 'none' ? (
                  <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger-text">
                    {nothing}
                  </p>
                ) : (
                  <p className="text-sm text-fg-muted">{nothing}</p>
                ))}
              <Notes
                lines={[
                  nameProblem === 'street' && addCount > 0 ? STREET_NAMES_TEXT : null,
                  noCoordinatesText(plan.withoutCoordinates),
                  unknownSourcesText(unknownSources, sourceName),
                ]}
              >
                {nameProblem === 'street' && addCount > 0 && (
                  <Checkbox size="sm" checked={streetNamesOk} onCheckedChange={setStreetNamesOk} label={STREET_NAMES_CONFIRM} />
                )}
              </Notes>

              <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <legend className="mb-2 text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">Options</legend>
                <Field label="Owner" hint={ownerHint(viewer?.role === 'admin')}>
                  <Select
                    value={owner}
                    onChange={(e) => setOwner(e.target.value)}
                    options={ownerOptions(people, viewer?.profileId)}
                    {...(owner === '' ? { placeholder: OWNER_PLACEHOLDER } : {})}
                  />
                </Field>
                <Field label="Lead source" hint={SOURCE_HINT}>
                  <Select value={sourceId} onChange={(e) => setSourceId(e.target.value)} options={sourceOptions(sources.values)} />
                </Field>
                <Field label="Record type" hint={TYPE_HINT}>
                  <Select value={typeValue} onChange={(e) => setTypeValue(e.target.value)} options={typeOptions()} />
                </Field>
              </fieldset>

              {usage && (
                <p className="text-xs text-fg-muted">
                  <span className="font-semibold text-fg">Columns used:</span> {usage.used.join(', ') || 'none'}
                  {usage.ignored.length > 0 && (
                    <>
                      {' · '}
                      <span className="font-semibold text-fg">Ignored:</span> {usage.ignored.join(', ')}
                    </>
                  )}
                </p>
              )}

              {preview.length > 0 && <PreviewTable rows={preview} total={addCount} />}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

function Notes({ lines, children }: { lines: readonly (string | null)[]; children?: ReactNode }) {
  const shown = lines.filter((l): l is string => l !== null);
  if (shown.length === 0) return null;
  return (
    <div role="note" className="flex items-start gap-3 rounded-card border border-warning/50 bg-warning-soft px-4 py-3 text-sm text-fg">
      <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warning-text" />
      <div className="flex flex-col gap-1.5">
        {shown.map((l) => (
          <p key={l}>{l}</p>
        ))}
        {children}
      </div>
    </div>
  );
}

const col = createColumnHelper<PreviewRow>();

const PREVIEW_COLUMNS: DataTableColumn<PreviewRow>[] = [
  col.accessor('name', {
    header: 'Name',
    size: 220,
    meta: { grow: 2 },
    cell: (c) => <span className="block truncate text-sm font-semibold text-fg">{c.getValue()}</span>,
  }),
  col.accessor('city', { header: 'City', size: 130, meta: { grow: 1 }, cell: (c) => <Cell text={c.getValue()} /> }),
  col.accessor('state', { header: 'State', size: 70, cell: (c) => <Cell text={c.getValue()} /> }),
  col.accessor('phone', { header: 'Phone', size: 140, cell: (c) => <Cell text={c.getValue()} /> }),
  col.accessor('tags', { header: 'Tags', size: 160, meta: { grow: 1 }, cell: (c) => <Cell text={c.getValue()} /> }),
];

function Cell({ text }: { text: string }) {
  return <span className={cn('block truncate text-sm', text === '' ? 'text-fg-subtle' : 'text-fg')}>{text === '' ? '—' : text}</span>;
}

function PreviewTable({ rows, total }: { rows: readonly PreviewRow[]; total: number }) {
  return (
    <section aria-labelledby="import-preview-title" className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 id="import-preview-title" className="text-sm font-semibold text-fg">
          First rows
        </h3>
        <span className="text-xs text-fg-muted">
          {total > rows.length
            ? `The first ${formatGrouped(Math.min(rows.length, PREVIEW_ROW_LIMIT))} of ${formatGrouped(total)} stores to add`
            : `${formatGrouped(rows.length)} ${rows.length === 1 ? 'store' : 'stores'} to add`}
        </span>
      </div>
      <DataTable
        data={rows}
        columns={PREVIEW_COLUMNS}
        getRowId={(r) => r.id}
        label="Stores to add"
        enableSorting={false}
        rowHeight={40}
        height={Math.min(280, 42 + rows.length * 40)}
      />
    </section>
  );
}
